import { ApolloError } from '@apollo/client'
import { Box, Grid, Spinner } from '@factorialco/deprecated-design-system'
import {
  AtsGetMainMetricsInsightsDocument,
  AtsGetMainMetricsInsightsQuery,
  AtsInsightsMetricsFilterFragment,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DataNotAvailable } from 'modules/ats/components/Insights/DataNotAvailable'
import { FunnelChart } from 'modules/ats/components/Insights/FunnelChart'
import { JobPostingsStateChart } from 'modules/ats/components/Insights/JobPostingsStateChart'
import { LastUpdatedAt } from 'modules/ats/components/Insights/LastUpdatedAt'
import { TimePerStageBarChart } from 'modules/ats/components/Insights/TimePerStageBarChart'
import { TimeToChart } from 'modules/ats/components/Insights/TimeToChart'
import { useExportUrlParams } from 'modules/ats/lib/utils/insights'

import { MetricsFilters } from '../MetricsFilters'
import { useMetricsFilters } from '../MetricsFiltersProvider'
import { insightsToObject, minutesToDays } from './utils'

interface Props {
  atsCompanyId: number
  jobs: AtsInsightsMetricsFilterFragment[]
}

interface InnerContentProps {
  loading: boolean
  error?: ApolloError
  data?: AtsGetMainMetricsInsightsQuery
  exportUrlParams: string[][]
}

const InnerContent = ({ data, error, loading, exportUrlParams }: InnerContentProps) => {
  if (!data || loading) {
    return (
      <Box padding="s40" width="full" alignItems="center" flexGrow>
        <Spinner />
      </Box>
    )
  }

  if (error?.message) {
    return (
      <Box minHeight="s240" justifyContent="center">
        <DataNotAvailable />
      </Box>
    )
  }

  const jobPostingsInsights = insightsToObject(data)
  const sortedStagesData = sortBy(
    data?.ats.hiringStagesInsightsConnection.nodes,
    (stage) => stage.hiringStage?.position
  )

  return (
    <>
      <Grid variant="large">
        <TimeToChart
          title={i18n.t('ats.insights_page.main_metrics.time_to_hire.title')}
          info={i18n.t('ats.insights_page.main_metrics.time_to_hire.info')}
          emptyState={i18n.t('ats.insights_page.empty_state.time_to_hire.description')}
          time={minutesToDays(jobPostingsInsights.hire_time.averageMinutes)}
        />
        <TimeToChart
          title={i18n.t('ats.insights_page.main_metrics.time_to_offer.title')}
          info={i18n.t('ats.insights_page.main_metrics.time_to_offer.info')}
          emptyState={i18n.t('ats.insights_page.empty_state.time_to_offer.description')}
          time={minutesToDays(jobPostingsInsights.offer_time.averageMinutes)}
        />
      </Grid>

      <JobPostingsStateChart data={jobPostingsInsights} />
      {sortedStagesData && (
        <>
          <FunnelChart stagesData={sortedStagesData} />
          <TimePerStageBarChart
            stagesData={sortedStagesData}
            exportUrlParams={exportUrlParams}
            onExport={() => {
              track(trackingEvents.ats.insightsHiringStagesReportDownload)
            }}
          />
        </>
      )}
    </>
  )
}

export const MainMetrics = ({ atsCompanyId, jobs }: Props) => {
  const { filters } = useMetricsFilters()
  const { exportUrlParams } = useExportUrlParams({ atsCompanyId, filters })

  useEffect(() => {
    track(trackingEvents.ats.insightsMainPageView)
  }, [])

  const { loading, error, data } = useQuery(AtsGetMainMetricsInsightsDocument, {
    variables: { ...filters, atsCompanyId },
  })

  const lastUpdatedAts = data?.ats.lastUpdatedAtInsightsConnection.nodes
  return (
    <Box paddingY="s24" gap="s24">
      <Box flexDirection="row" gap="s16">
        <MetricsFilters jobs={jobs} />
        <LastUpdatedAt lastUpdatedAts={lastUpdatedAts} id={'job_postings'} />
      </Box>
      <InnerContent loading={loading} error={error} data={data} exportUrlParams={exportUrlParams} />
    </Box>
  )
}
